import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

/**
 * Hook for screening-scoped candidate search.
 * Calls POST /api/screenings/screening/:screeningId/ai-search and manages query, results, and loading state.
 *
 * Only the latest search may update state: starting a new search or clearing aborts the
 * in-flight request, so a slow earlier response can never overwrite a newer one.
 *
 * @param {string|null|undefined} screeningId - Screening ID (search is a no-op when falsy)
 * @returns {{
 *   query: string,
 *   setQuery: (v: string) => void,
 *   results: Array|null,
 *   isActive: boolean,
 *   isSearching: boolean,
 *   handleSearch: (q?: string) => Promise<void>,
 *   clear: () => void
 * }}
 */
export function useAISearch(screeningId) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const abortRef = useRef(null);

  const cancelInFlight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const clear = useCallback(() => {
    cancelInFlight();
    setQuery('');
    setResults(null);
    setIsActive(false);
    setIsSearching(false);
  }, [cancelInFlight]);

  // A search belongs to one screening; drop it when navigating to another or unmounting.
  useEffect(() => clear, [screeningId, clear]);

  const handleSearch = useCallback(
    async (searchQuery) => {
      const trimmed = typeof searchQuery === 'string' ? searchQuery.trim() : query.trim();
      if (!trimmed) {
        clear();
        return;
      }
      if (!screeningId) {
        toast.error('Screening context is required for search');
        return;
      }

      cancelInFlight();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsSearching(true);
      setQuery(trimmed);

      try {
        const response = await axios.post(
          `/api/screenings/screening/${screeningId}/ai-search`,
          { query: trimmed },
          { signal: controller.signal }
        );
        if (controller.signal.aborted) return;

        const candidates = Array.isArray(response.data?.candidates) ? response.data.candidates : [];
        setResults(candidates);
        setIsActive(true);

        const total = candidates.length;
        if (total > 0) {
          toast.success(`Found ${total} candidate${total !== 1 ? 's' : ''} matching your search`);
        } else {
          toast('No candidates found matching your search');
        }
      } catch (error) {
        if (axios.isCancel(error) || controller.signal.aborted) return;
        console.error('AI search error:', error);
        toast.error(error.response?.data?.message || 'Failed to perform search');
        setResults(null);
        setIsActive(false);
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setIsSearching(false);
        }
      }
    },
    [screeningId, query, clear, cancelInFlight]
  );

  return {
    query,
    setQuery,
    results,
    isActive,
    isSearching,
    handleSearch,
    clear,
  };
}
